<script setup lang="ts">
import { computed, useSlots, useTemplateRef, onMounted, ref, watch } from "vue";
import type { UIColor } from "../../..";
import Overlay from "../../internal/Overlay/Overlay.vue";
import Copy from "../Copy/Copy.vue";
import Icon from "../Icon/Icon.vue";

defineOptions({
  inheritAttrs: false,
});

export type TextFieldProps<T extends number | string> = {
  placeholder?: string;
  colorBackground?: UIColor;
  colorForeground?: UIColor;
  colorPlaceholder?: UIColor;
  colorBorder?: UIColor;
  colorErrorPlaceholder?: UIColor;
  colorErrorForeground?: UIColor;
  colorErrorBackground?: UIColor;
  minWidth?: string;
  maxWidth?: string;
  textType?: "body" | "number";
  characterCountLimit?: number;
  validate?: (value: T | undefined | null) => boolean;
  disabled?: boolean;
  autoFocus?: boolean;
  autoResize?: boolean;
  textAlignEnd?: boolean;
  multiLine?: boolean;
  noBorder?: boolean;
  chromeSearch?: boolean;
  clearButton?: boolean;
};

const props = withDefaults(defineProps<TextFieldProps<string>>(), {
  textType: "body",
  placeholder: "",
  colorForeground: "--color-primary",
  colorBackground: "--color-accent-100",
  colorPlaceholder: "--color-accent-300",
  colorBorder: "--color-accent-200",
  colorErrorBackground: "--color-red-100",
  colorErrorForeground: "--color-red-300",
  colorErrorPlaceholder: "--color-red-200",
  minWidth: "0",
  maxWidth: "100%",
  autoFocus: false,
  autoResize: false,
  textAlignEnd: false,
  multiLine: false,
  noBorder: false,
  chromeSearch: false,
  clearButton: false,
  characterCountLimit: 524288,
});

const model = defineModel<string | null>();
const error = defineModel("error", { default: false });

const _value = computed({
  get: () => model.value ?? "",
  set: (v) => (model.value = v || undefined),
});

const emit = defineEmits<{
  (event: "validate-success", value: string | number | null | undefined): void;
  (event: "validate-error", value: string | number | null | undefined): void;
}>();

const slots = useSlots();

const container = useTemplateRef("container");
const input = useTemplateRef("input");
const textarea = useTemplateRef("textarea");
const textareaResize = useTemplateRef("textareaResize");

const shouldSelect = ref(true);
const height = ref(0);

const characterCountText = computed(
  () => `${_value.value.length} / ${props.characterCountLimit}`
);

const iconBeforePopulated = computed(() => slots.iconBefore !== undefined);
const iconAfterPopulated = computed(() => slots.iconAfter !== undefined);

const resetShouldFocus = () => (shouldSelect.value = true);

const selectText = computed(() => {
  const _shouldSelect = shouldSelect.value;
  const _input = input.value;

  return () => {
    if (_shouldSelect && _input) {
      _input.select();

      shouldSelect.value = false;
    }
  };
});

const runValidate = (value: string | null | undefined) => {
  if (!props.validate) return;

  if (props.validate(value)) {
    error.value = false;

    emit("validate-success", value);
  } else {
    error.value = true;

    emit("validate-error", value);
  }
};

const handleInput = (event: Event) => {
  const newValue = (event.target as HTMLInputElement).value;

  _value.value = newValue;

  runValidate(model.value);
};

const blur = () => {
  input.value?.blur();
  textarea.value?.blur();

  shouldSelect.value = true;
};

const clear = () => (_value.value = "");

const resize = () => {
  requestAnimationFrame(() => {
    if (textareaResize.value) {
      height.value = textareaResize.value.scrollHeight;
    }
  });
};

onMounted(() => {
  if (props.autoFocus) {
    input.value?.focus();
    textarea.value?.focus();
  }
});

watch(
  model,
  (v) => {
    runValidate(v);
    props.autoResize && resize();
  },
  { immediate: true }
);

defineExpose({ container, blur });
</script>

<template>
  <div ref="container" :class="[$style.container, disabled && $style.disabled]">
    <Overlay
      v-if="disabled"
      colorBackground="--color-primary"
      colorForeground="--color-primary"
      opacityBackground="0.075"
      opacityForeground="0.1"
    />

    <div
      :class="[
        $style.containerInput,
        chromeSearch && $style.chromeSearch,
        multiLine && $style.multiLine,
        autoResize && $style.autoResize,
      ]"
      :style="{
        '--text-field-color-background': `var(${
          error ? colorErrorBackground : colorBackground
        })`,
        '--text-field-color-foreground': `var(${
          error ? colorErrorForeground : colorForeground
        })`,
        '--text-field-color-placeholder': `var(${
          error ? colorErrorPlaceholder : colorPlaceholder
        })`,
        '--text-field-color-border': `var(${
          error ? colorErrorForeground : colorBorder
        })`,
        'min-width': minWidth,
        'max-width': maxWidth,
      }"
    >
      <div v-if="iconBeforePopulated" :class="$style.iconBefore">
        <slot name="iconBefore" />
      </div>

      <input
        v-if="!multiLine"
        ref="input"
        spellcheck="false"
        type="text"
        :maxlength="characterCountLimit"
        :class="[
          $style.input,
          chromeSearch && $style.chromeSearch,
          $style[textType],
          $style[textAlignEnd ? 'textAlignEnd' : ''],
        ]"
        :placeholder="placeholder"
        :value="_value"
        @click="selectText()"
        @blur="resetShouldFocus()"
        v-bind="{ ...$attrs, onInput: handleInput }"
      />

      <textarea
        v-if="multiLine && !autoResize"
        ref="textarea"
        spellcheck="false"
        :value="_value"
        :maxlength="characterCountLimit"
        :class="[$style.input, $style.textarea, $style[textType]]"
        :placeholder="placeholder"
        v-bind="{ ...$attrs, onInput: handleInput }"
      />

      <div
        v-if="multiLine && autoResize"
        :class="[$style.input]"
        style="display: grid; grid-template-areas: 'textarea'"
      >
        <textarea
          ref="textarea"
          spellcheck="false"
          :value="_value"
          type="text"
          :maxlength="characterCountLimit"
          :class="[$style.input, $style.textarea, $style[textType]]"
          :placeholder="placeholder"
          :style="{
            height: autoResize ? `${height}px` : undefined,
            gridArea: 'textarea',
          }"
          v-bind="{ ...$attrs, onInput: handleInput }"
        />
        <textarea
          ref="textareaResize"
          :class="[$style.input, $style.textarea, $style[textType]]"
          :style="{
            height: '1.5rem',
            opacity: 0,
            pointerEvents: 'none',
            whiteSpace: 'pre-wrap',
            resize: 'none',
            gridArea: 'textarea',
          }"
          :placeholder="placeholder"
          :value="_value"
          type="text"
          readOnly
        />
      </div>

      <div
        v-if="characterCountLimit !== 524288"
        :class="[
          $style.iconAfter,
          $style.characterCount,
          _value.length > 0 && $style.visible,
        ]"
      >
        <Copy type="label" size="small" :color="colorForeground">
          {{ characterCountText }}
        </Copy>
      </div>

      <div
        v-if="clearButton && _value"
        :class="[$style.iconAfter, $style.clear]"
        @click="clear"
      >
        <Icon id="close" :color="colorForeground" size="medium" />
      </div>

      <div v-if="iconAfterPopulated" :class="$style.iconAfter">
        <slot name="iconAfter" />
      </div>

      <div
        :class="[
          $style.background,
          noBorder && $style.noBorder,
          chromeSearch && $style.chromeSearch,
        ]"
      />
    </div>
  </div>
</template>

<style module>
.container {
  display: flex;
  position: relative;
  width: 100%;
}

.container.disabled {
  cursor: not-allowed;
}

.containerInput {
  display: flex;
  position: relative;
  width: 100%;
  min-width: 8rem;
  min-height: 2.5rem;
  gap: 0.5rem;
  align-items: center;
  padding: 0.25rem 0.75rem;
  background-color: var(--text-field-color-background);
  border: solid 1px var(--text-field-color-border);
}

.multiLine:not(.autoResize) {
  align-items: flex-start;
}

.multiLine.autoResize {
  min-height: 1.5rem;
}

.multiLine:not(.autoResize) .iconBefore {
  transform: translateX(-0.25rem) translateY(calc(0.25rem * 1.5));
}

.multiLine:not(.autoResize) .iconAfter {
  transform: translateX(0.25rem) translateY(calc(0.25rem * 1.5));
}

.background {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: -1;
}

.noBorder {
  border-bottom: none;
}

.input,
.input::placeholder {
  font-family: "Albert Sans";
}

.input:-webkit-autofill {
  -webkit-box-shadow: 0 0 0 1000px var(--text-field-color-background) inset !important;
  -webkit-text-fill-color: var(--text-field-color-foreground) !important;
}

.input.body {
  font-family: "Albert Sans";
}

.input {
  appearance: none;
  display: inline-flex;
  width: 100%;
  height: 100%;
  font-size: 1rem;
  font-weight: 400;
  line-height: 1rem;
  color: var(--text-field-color-foreground);
  border: none;
  background-color: var(--text-field-color-background);
  resize: none;
  outline: none;
}

input::selection {
  background-color: var(--text-field-color-placeholder);
}

.input::placeholder {
  font-size: 14px;
  font-weight: 500;
  color: var(--text-field-color-placeholder);
}

.textarea {
  line-height: calc(1rem * 1.48);
  height: calc(1rem * 1.48 * 4);
  resize: none;
  margin-top: 0.25rem;
  margin-bottom: 0.25rem;
}

.autoResize .textarea {
  padding: 0;
  line-height: 1.5rem;
  overflow: hidden;
}

.textAlignEnd {
  text-align: right;
}

.iconBefore,
.iconAfter {
  display: inline-flex;
  width: fit-content;
  transform: translateX(-0.25rem);
  user-select: none;
}

.iconAfter {
  transform: translateX(0.25rem);
}

.characterCount {
  opacity: 0;
  transition-property: opacity;
  transition-duration: var(--transition-duration-short);
  transition-timing-function: ease-in-out;
}

.characterCount.visible {
  opacity: 1;
}

.containerInput.chromeSearch {
  clip-path: none;
  background-color: var(--color-accent-100);
}

/* chromeSearch - only used within /web/components/Chrome */
.background.chromeSearch {
  border-bottom: none;
}

.clear:hover {
  cursor: pointer;
  opacity: 0.5;
}
</style>
